import React, { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import Checkbox from '../../common/Checkbox';
import { TermsAndConditionsLegacy } from './TermsAndConditionsLegacy';

import {
  AGREE_TERMS_AND_CONDITION_CHANGED,
  CONSENT_ELECTRONIC_DELIVERY_CHANGED,
  UNDERSTAND_CONDITIONS_CHANGED,
} from '../../../actions/types';
import {
  termsAndCondModalVisibleChanged,
  validateTermsAndConditions,
} from '../../../actions/quoting';
import { ELECTRONIC_DELIVERY_NOTICE } from '../../../constants';
import { useElectronicConsent } from '../../../util';
import { useTermsAndConditions } from '../../../hooks/useFeatureFlag';

import './TermsAndConditionsCheckbox.css';
import './TermsAndConditions.css';

const TermsAndConditions = ({
  containerRef = null,
  triggerValidation,
}) => {
  const { t } = useTranslation('enrollment');
  const newTermsAndConditions = useTermsAndConditions();
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const { rateSummary: { PetQuotes } } = store;
  const {
    agreePaymentAndRenewalCheck,
    agreePaymentAndRenewalError,
    agreeTermsAndConditionCheck,
    agreeTermsAndConditionError,
    consentElectronicDeliveryCheck,
  } = store.termsAndConditions;
  const dispatch = useDispatch();

  useEffect(() => {
    if (triggerValidation) {
      dispatch(validateTermsAndConditions());
    }
  }, [triggerValidation, dispatch]);

  function isElectronicConsent() {
    if (PetQuotes && PetQuotes[0]) {
      return PetQuotes[0].IsElectronicConsent;
    }

    return false;
  }

  const handleAgreeTermsClick = useCallback(() => {
    dispatch({ type: AGREE_TERMS_AND_CONDITION_CHANGED });
  }, [dispatch]);

  const handleConsentDeliveryClick = useCallback(() => {
    dispatch({ type: CONSENT_ELECTRONIC_DELIVERY_CHANGED });
  }, [dispatch]);

  const handleUnderstandConditionClick = useCallback(() => {
    dispatch({ type: UNDERSTAND_CONDITIONS_CHANGED });
  }, [dispatch]);

  const handleTermsAndConditionsModalOpen = useCallback(() => {
    const categoryId = 8;
    const visible = true;
    dispatch(termsAndCondModalVisibleChanged({ categoryId, visible }));
  }, [dispatch]);

  const handleUnderstandConditionModalOpen = useCallback(() => {
    const categoryId = 9;
    const visible = true;
    dispatch(termsAndCondModalVisibleChanged({ categoryId, visible }));
  }, [dispatch]);

  const handlePaymentAndRenewaModalOpen = useCallback(() => {
    const categoryId = 8;
    const visible = true;
    dispatch(termsAndCondModalVisibleChanged({ categoryId, visible }));
  }, [dispatch]);

  const renderElectronicConsent = () => {
    if (useElectronicConsent || isElectronicConsent()) {
      return (
        <Checkbox
          checked={consentElectronicDeliveryCheck}
          className="Terms-and-condition-checkbox"
          id="electronicDeliveryNotice"
          onClick={handleConsentDeliveryClick}
        >
          <Trans
            components={{
              a: (
                // eslint-disable-next-line max-len
                // eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
                <a
                  className="Terms-and-condition-child-modal-clickable"
                  href={ELECTRONIC_DELIVERY_NOTICE}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
            }}
            i18nKey="termsAndConditions.electronicDelivery"
            ns="enrollment"
            t={t}
          />
        </Checkbox>
      );
    }

    return null;
  };

  if (!newTermsAndConditions) {
    return (
      <TermsAndConditionsLegacy
        agreePaymentAndRenewalCheck={agreePaymentAndRenewalCheck}
        agreePaymentAndRenewalError={agreePaymentAndRenewalError}
        agreeTermsAndConditionCheck={agreeTermsAndConditionCheck}
        agreeTermsAndConditionError={agreeTermsAndConditionError}
        consentElectronicDeliveryCheck={consentElectronicDeliveryCheck}
        containerRef={containerRef}
        handleAgreeTermsClick={handleAgreeTermsClick}
        handleConsentDeliveryClick={handleConsentDeliveryClick}
        handleTermsAndConditionsModalOpen={handleTermsAndConditionsModalOpen}
        handleUnderstandConditionClick={handleUnderstandConditionClick}
        handleUnderstandConditionModalOpen={handleUnderstandConditionModalOpen}
        shouldRenderElectronicConsent={useElectronicConsent
          || isElectronicConsent()}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={'Your-info-agreements-container '
          + 'Terms-and-condition-checkbox'}
      id="termsAndConditions"
      tabIndex={-1}
    >
      <h1 className="Your-info-agreements-title">
        {t('termsAndConditions.title')}
      </h1>

      {renderElectronicConsent()}

      <Checkbox
        anounceError={false}
        checked={agreeTermsAndConditionCheck}
        className="Terms-and-condition-checkbox"
        containerClassName="Terms-and-condition-checkbox-container"
        error={agreeTermsAndConditionError}
        errorMessage="Required. Please confirm statement to proceed."
        id="iAgreeTerms"
        onClick={handleAgreeTermsClick}
      >
        <Trans
          components={{
            button: (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                aria-controls="termsAndConditionmodalContainer"
                aria-haspopup="dialog"
                className="Terms-and-condition-child-modal-clickable"
                onClick={handleTermsAndConditionsModalOpen}
                type="button"
              />
            ),
            button2: (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                aria-controls="termsAndConditionmodalContainer"
                aria-haspopup="dialog"
                className="Terms-and-condition-child-modal-clickable"
                onClick={handleUnderstandConditionModalOpen}
                type="button"
              />
            ),
          }}
          i18nKey="termsAndConditions.termsAndConditions"
          ns="enrollment"
          t={t}
        />
      </Checkbox>

      <Checkbox
        anounceError={false}
        checked={agreePaymentAndRenewalCheck}
        className="Terms-and-condition-checkbox"
        containerClassName="Terms-and-condition-checkbox-container"
        error={agreePaymentAndRenewalError}
        errorMessage="Required. Please confirm statement to proceed."
        id="paymentAndRenewalTerms"
        onClick={handleUnderstandConditionClick}
      >
        <Trans
          components={{
            button: (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                aria-controls="termsAndConditionmodalContainer"
                aria-haspopup="dialog"
                className="Terms-and-condition-child-modal-clickable"
                onClick={handlePaymentAndRenewaModalOpen}
                type="button"
              />
            ),
          }}
          i18nKey="termsAndConditions.paymentRenewal"
          ns="enrollment"
          t={t}
        />
      </Checkbox>
    </div>
  );
};

export default TermsAndConditions;
